import type { Placement } from '@floating-ui/react-dom';
import classNames from 'classnames';
import React from 'react';
import * as styles from './ControlBarPopOver.module.scss';
import PopOverArrow from '../../components/PopOverArrow';
import PopOverPrimitive from '../../components/PopOverPrimitive';
import resizeOnShow from '../../utils/popover/resizeOnShow';

const OFFSET = 14;
const SCREEN_PADDING = 12;

interface ControlBarPopOverProps {
  children: React.ReactNode;
  content: React.ReactNode;
  open: boolean;
  placement: Placement;
  onOpenChange: (open: boolean) => void;
  preventScreenOverflow?: boolean | { minHeight?: number };
  arrowClassName?: string;
  anchorRef?: React.ComponentProps<typeof PopOverPrimitive>['anchorRef'];
  belowPageChrome?: boolean;
}

const ControlBarPopOver = ({
  content,
  open,
  children,
  placement,
  onOpenChange,
  arrowClassName,
  anchorRef,
  preventScreenOverflow,
  belowPageChrome,
}: ControlBarPopOverProps) => {
  return (
    <PopOverPrimitive
      onOpenChange={onOpenChange}
      open={open}
      offset={OFFSET}
      screenPadding={SCREEN_PADDING}
      placement={placement}
      trigger="click"
      autoUpdateAnimationFrame
      noFlip
      hideWhenReferenceHidden={!belowPageChrome}
      anchorRef={anchorRef}
      getAdditionalMiddleware={
        preventScreenOverflow
          ? (options) => [
              resizeOnShow({
                padding: options.padding,
                minHeight:
                  typeof preventScreenOverflow === 'object'
                    ? preventScreenOverflow.minHeight
                    : undefined,
              }),
            ]
          : undefined
      }
    >
      <PopOverPrimitive.Trigger asChild>{children}</PopOverPrimitive.Trigger>
      <PopOverPrimitive.Floating
        className={classNames(styles.floatingContainer, {
          [styles.isBelowPageChrome]: belowPageChrome,
        })}
        noPortal={belowPageChrome}
      >
        <PopOverPrimitive.Content className={styles.inner}>
          {content}
        </PopOverPrimitive.Content>
        <PopOverArrow className={arrowClassName} />
      </PopOverPrimitive.Floating>
    </PopOverPrimitive>
  );
};

ControlBarPopOver.Anchor = PopOverPrimitive.Anchor;

export default ControlBarPopOver;
